import React, { useContext, useEffect, useState } from 'react';
import {
  confirmPasswordReset,
  verifyPasswordResetCode,
  reauthenticateWithCredential,
  getMultiFactorResolver,
  EmailAuthProvider,
  updatePassword,
} from 'firebase/auth';
import type { MultiFactorError, MultiFactorResolver } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from '@mui/material';
import { auth, functions } from '../../../api/firebase/config';
import { httpsCallable } from 'firebase/functions';
import { gray100, primaryButtonBackgroundColor, gray40, white } from '../../../constants/colors';
import { headerHeight, spHeaderHeight } from '../../../constants/location';
import { AuthContext } from '../../../contexts/auth';
import { PasswordChangeSchema, LoggedInPasswordChangeSchema } from '../../../validation/user';
import type { PasswordChangeType, LoggedInPasswordChangeType } from '../../../types/user';

import CustomTextfield from '../../uiParts/form/CustomTextfield';
import Header from '../../uiParts/menu/Header';
import PasswordTextField from '../../uiParts/form/PasswordTextfield';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import CustomLoading from '../../uiParts/form/CustomLoading';
import { AxiosError } from 'axios';
import { getErrorMessageFromAuthErrorCode } from '../../../api/firebase/auth/error';
import { authError, common } from '../../../modules/error';
import MetaTitle from '../../utils/MetaTitle';
import MfaReauthDialog from '../../uiParts/mfa/MfaReauthDialog';
import { useProxyAccessControl } from '../../../hooks/useProxyAccessControl';

const styles = {
  button: {
    width: '400px',
    height: '44px',
    fontSize: '16px',
    fontWeight: '700',
    '@media screen and (max-width: 600px)': {
      width: '100%',
    },
  },
  pageContainer: {
    pt: headerHeight,
    minHeight: '100vh',
    '@media screen and (max-width: 600px)': {
      pt: spHeaderHeight,
      minHeight: '100vh',
    },
  },
  PasswordChangeContainer: {
    alignItems: 'center',
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    flexDirection: 'column',
    p: '39px',
    '@media screen and (max-width: 600px)': {
      width: '100%',
      padding: '32px 16px 34px 16px',
      boxSizing: 'border-box',
    },
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    '@media screen and (max-width:600px)': {
      width: '100%',
      padding: '0 16px',
      boxSizing: 'border-box',
    },
    width: '560px',
    margin: '0 auto',
  },
  link: {
    color: gray100,
    display: 'block',
    fontWeight: '500',
    m: '24px 0px 0px 0px',
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '20px',
    },
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    mb: '24px',
    textAlign: 'left',
  },
};

const PasswordChange: React.FC = (): JSX.Element => {
  useProxyAccessControl();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [isLoggedInMode, setIsLoggedInMode] = useState(false);
  // 二要素認証（#3282）: 再認証が multi-factor-auth-required になったときの resolver
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const { user, isLoading: isAuthLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  // パスワードリセット用フォーム（未ログイン）
  const resetForm = useForm<PasswordChangeType>({
    resolver: yupResolver(PasswordChangeSchema),
    defaultValues: {
      email: email,
    },
  });

  // パスワード変更用フォーム（ログイン済み）
  const changeForm = useForm<LoggedInPasswordChangeType>({
    resolver: yupResolver(LoggedInPasswordChangeSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
    },
  });

  const buttonDisabledFlag = (): boolean => {
    if (!firstSubmit) return false;

    if (isLoggedInMode) {
      const { currentPassword, newPassword, newPasswordConfirm } = changeForm.watch();
      return (
        !currentPassword ||
        !newPassword ||
        !newPasswordConfirm ||
        Boolean(changeForm.formState.errors.currentPassword) ||
        Boolean(changeForm.formState.errors.newPassword) ||
        Boolean(changeForm.formState.errors.newPasswordConfirm) ||
        newPassword !== newPasswordConfirm
      );
    }

    return (
      resetForm.watch('password') === undefined ||
      resetForm.watch('password') === '' ||
      resetForm.watch('passwordConfirm') === undefined ||
      resetForm.watch('passwordConfirm') === '' ||
      Boolean(resetForm.formState.errors.password) ||
      Boolean(resetForm.formState.errors.passwordConfirm) ||
      resetForm.watch('password') !== resetForm.watch('passwordConfirm')
    );
  };

  useEffect(() => {
    // 認証状態の確認中は何もしない
    if (isAuthLoading) return;

    const queryParams = new URLSearchParams(location.search);
    const oobCode = queryParams.get('oobCode');
    const token = queryParams.get('token');

    // トークンがない場合でログイン済みの場合はログイン済みモード
    if (!oobCode && !token && user) {
      setIsLoggedInMode(true);
      return;
    }

    // トークンがない場合で未ログインの場合はパスワードリセット画面へ
    if (!oobCode && !token && !user) {
      navigate('/auth/password_reset');
      return;
    }

    if (oobCode) {
      // Firebase標準のパスワードリセット処理
      setCode(oobCode);
      setLoading(true);
      verifyPasswordResetCode(auth, oobCode)
        .then(email => {
          setEmail(email);
        })
        .catch(error => {
          if (error instanceof FirebaseError && error.code === 'auth/network-request-failed') {
            setErrorText(authError.networkRequestFailed);
            setOpen(true);
          } else if (error instanceof FirebaseError && error.code === 'auth/expired-action-code') {
            setErrorText(
              'リンクの期限が切れています。パスワード再設定のメール送信から再度お試しください。',
            );
            setOpen(true);
            setTimeout(() => {
              navigate('/auth/password_reset', { state: { email: email } });
            }, 3000);
          } else {
            setErrorText('エラーが発生しました。');
            setOpen(true);
            setTimeout(() => {
              navigate('/auth/password_reset', { state: { email: email } });
            }, 3000);
          }
        })
        .finally(() => setLoading(false));
    } else if (token) {
      // SendGrid用のトークン処理
      setCode(token);
      setLoading(true);

      // トークンからメールアドレスを取得するための関数を呼び出し
      const verifyToken = httpsCallable<
        { token: string },
        { success: boolean; email?: string; message: string }
      >(functions, 'verifyPasswordResetToken');

      verifyToken({ token })
        .then(result => {
          if (result.data.success && result.data.email) {
            setEmail(result.data.email);
          } else {
            setErrorText(
              'パスワードリセットのURLが無効です。\nパスワード再設定をやり直してください。',
            );
            setOpen(true);
            setTimeout(() => {
              navigate('/auth/password_reset');
            }, 3000);
          }
        })
        .catch(error => {
          const isNetworkError =
            (error instanceof FirebaseError &&
              (error.code === 'auth/network-request-failed' ||
                error.code === 'functions/unavailable')) ||
            (error instanceof Error && error.message === 'Failed to fetch');

          if (isNetworkError) {
            setErrorText(authError.networkRequestFailed);
            setOpen(true);
          } else if (error?.message?.includes('有効期限が切れています')) {
            setErrorText(
              'パスワードリセットのURLの有効期限が切れています。\nパスワード再設定をやり直してください。',
            );
            setOpen(true);
            setTimeout(() => {
              navigate('/auth/password_reset');
            }, 3000);
          } else if (error?.message?.includes('無効なトークン')) {
            setErrorText(
              'パスワードリセットのURLが無効です。\nパスワード再設定をやり直してください。',
            );
            setOpen(true);
            setTimeout(() => {
              navigate('/auth/password_reset');
            }, 3000);
          } else {
            setErrorText(
              'パスワードリセット処理でエラーが発生しました。\nパスワード再設定をやり直してください。',
            );
            setOpen(true);
            setTimeout(() => {
              navigate('/auth/password_reset');
            }, 3000);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [user, isAuthLoading, navigate]);

  // パスワード再設定処理（未ログイン）
  const onSubmitReset = async (): Promise<void> => {
    try {
      setLoading(true);

      // URLパラメータからtokenかoobCodeかを判定
      const queryParams = new URLSearchParams(location.search);
      const isTokenBased = queryParams.has('token');

      if (isTokenBased) {
        // SendGrid用のトークンベース処理
        const changePasswordWithToken = httpsCallable<
          { token: string; newPassword: string },
          { success: boolean; message: string }
        >(functions, 'changePasswordWithToken');

        await changePasswordWithToken({
          token: code,
          newPassword: resetForm.watch('password'),
        });
      } else {
        // Firebase標準のoobCodeベース処理
        await confirmPasswordReset(auth, code, resetForm.watch('password'));
      }

      navigate('/auth/password_change/complete');
    } catch (error) {
      let errorMessage: string;
      if (error instanceof FirebaseError) {
        if (
          error.code === 'auth/network-request-failed' ||
          error.code === 'functions/unavailable'
        ) {
          errorMessage = authError.networkRequestFailed;
        } else {
          errorMessage = getErrorMessageFromAuthErrorCode(error.code);
        }
      } else if (error instanceof AxiosError && error.response) {
        errorMessage = error.response.data.message;
      } else {
        errorMessage = common;
      }
      setErrorText(errorMessage);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // パスワード変更処理で発生したエラーを画面に表示する
  const showChangeError = (error: unknown): void => {
    let errorMessage: string;
    if (error instanceof FirebaseError) {
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMessage = '現在のパスワードが正しくありません。';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = authError.networkRequestFailed;
      } else {
        errorMessage = getErrorMessageFromAuthErrorCode(error.code);
      }
    } else {
      errorMessage = common;
    }
    setErrorText(errorMessage);
    setOpen(true);
  };

  // 再認証が済んだ状態で新しいパスワードに更新する
  const updateToNewPassword = async (): Promise<void> => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('ユーザー情報が取得できません');
    }
    await updatePassword(currentUser, changeForm.watch('newPassword'));
    navigate('/auth/password_change/complete');
  };

  // パスワード変更処理（ログイン済み）
  const onSubmitChange = async (): Promise<void> => {
    try {
      setLoading(true);

      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error('ユーザー情報が取得できません');
      }

      const { currentPassword } = changeForm.watch();

      // 現在のパスワードで再認証
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      try {
        await reauthenticateWithCredential(currentUser, credential);
      } catch (error) {
        // 認証アプリ（TOTP）登録済みのユーザーは、パスワードだけの再認証が
        // multi-factor-auth-required で中断される。確認コードの入力を求め、
        // 第二要素の解決後にダイアログ側からパスワード更新を続行する（#3282）
        if (error instanceof FirebaseError && error.code === 'auth/multi-factor-auth-required') {
          setMfaResolver(getMultiFactorResolver(auth, error as MultiFactorError));
          return;
        }
        throw error;
      }

      // 新しいパスワードに更新
      await updateToNewPassword();
    } catch (error) {
      showChangeError(error);
    } finally {
      setLoading(false);
    }
  };

  // 二要素認証の確認コード検証後にパスワード更新を続行する（#3282）
  const onMfaVerified = async (): Promise<void> => {
    try {
      setLoading(true);
      await updateToNewPassword();
      setMfaResolver(null);
    } finally {
      // 更新に失敗した場合はダイアログを開いたままにする。
      // エラーの表示はダイアログ側が行うため、ここでは握り潰さず送出する
      setLoading(false);
    }
  };

  // ログイン済みユーザー向けのフォーム
  const renderLoggedInForm = (): JSX.Element => (
    <>
      <Controller
        key="currentPassword"
        render={({ field }): JSX.Element => (
          <PasswordTextField
            {...field}
            error={Boolean(changeForm.formState.errors.currentPassword)}
            formMb="24px"
            helperText={changeForm.formState.errors.currentPassword?.message}
            labelText="現在のパスワード"
            requiredText="必須"
            width="400px"
          />
        )}
        control={changeForm.control}
        name="currentPassword"
      />

      <Controller
        key="newPassword"
        render={({ field }): JSX.Element => (
          <PasswordTextField
            {...field}
            error={Boolean(changeForm.formState.errors.newPassword)}
            formMb="24px"
            helperText={changeForm.formState.errors.newPassword?.message}
            labelText="新しいパスワード"
            requiredText="必須"
            width="400px"
          />
        )}
        control={changeForm.control}
        name="newPassword"
      />

      <Controller
        key="newPasswordConfirm"
        render={({ field }): JSX.Element => (
          <PasswordTextField
            {...field}
            error={Boolean(changeForm.formState.errors.newPasswordConfirm)}
            formMb="40px"
            helperText={changeForm.formState.errors.newPasswordConfirm?.message}
            labelText="新しいパスワード（確認用）"
            requiredText="必須"
            width="400px"
          />
        )}
        control={changeForm.control}
        name="newPasswordConfirm"
      />

      <Button
        disabled={buttonDisabledFlag()}
        onClick={async (): Promise<void> => {
          setFirstSubmit(true);
          await changeForm.handleSubmit(async () => {
            await onSubmitChange();
          })();
        }}
        sx={[primaryButtonBackgroundColor, styles.button]}
        variant="contained"
      >
        パスワードを変更する
      </Button>
    </>
  );

  // パスワードリセット用のフォーム（未ログイン）
  const renderResetForm = (): JSX.Element => (
    <>
      <Controller
        render={({ field }): JSX.Element => (
          <CustomTextfield
            {...field}
            formMb="24px"
            labelText="メールアドレス"
            placeholder={email}
            requiredText="必須"
            width="400px"
            disabled
          />
        )}
        control={resetForm.control}
        name="email"
      />

      <Controller
        render={({ field }): JSX.Element => (
          <PasswordTextField
            {...field}
            error={Boolean(resetForm.formState.errors.password)}
            formMb="24px"
            helperText={resetForm.formState.errors.password?.message}
            labelText="パスワード"
            requiredText="必須"
            width="400px"
          />
        )}
        control={resetForm.control}
        name="password"
      />

      <Controller
        render={({ field }): JSX.Element => (
          <PasswordTextField
            {...field}
            error={Boolean(resetForm.formState.errors.passwordConfirm)}
            formMb="40px"
            helperText={resetForm.formState.errors.passwordConfirm?.message}
            labelText="パスワード確認用"
            requiredText="必須"
            width="400px"
          />
        )}
        control={resetForm.control}
        name="passwordConfirm"
      />

      <Button
        disabled={buttonDisabledFlag()}
        onClick={async (): Promise<void> => {
          setFirstSubmit(true);
          await resetForm.handleSubmit(async () => {
            await onSubmitReset();
          })();
        }}
        sx={[primaryButtonBackgroundColor, styles.button]}
        variant="contained"
      >
        パスワードを再設定する
      </Button>
    </>
  );

  return (
    <Typography component="div" sx={styles.pageContainer}>
      <MetaTitle />
      <CustomLoading loading={loading || isAuthLoading} />
      <Header />
      <Typography component="div" sx={styles.container}>
        <Typography component="div" sx={styles.PasswordChangeContainer}>
          <Typography sx={styles.title}>
            {isLoggedInMode ? 'パスワード変更' : 'パスワード再設定'}
          </Typography>
          {isLoggedInMode ? renderLoggedInForm() : renderResetForm()}
        </Typography>
      </Typography>
      <CustomSnackbar
        open={open}
        onClose={(): void => setOpen(false)}
        closeButton={(): void => setOpen(false)}
        text={errorText}
      />
      <MfaReauthDialog
        open={Boolean(mfaResolver)}
        resolver={mfaResolver}
        onVerified={onMfaVerified}
        onClose={(): void => setMfaResolver(null)}
      />
    </Typography>
  );
};

export default PasswordChange;
